// ============================================
// LIGA AJUPAM - Auth Context Provider
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Profile, UserSession, TeamWithDetails } from '../types';
import { loginWithDNI, logout as logoutService, getCurrentSession, refreshSession, saveSession } from '../services/api/auth';
import { validateAdminSession } from '../services/api/adminAuth';
import { getTeamById } from '../services/api/teams';
import { supabase } from '../services/api/client';
import { withTimeout } from '../utils/withTimeout';
import { useAppResume } from '../hooks/useAppResume';
import { queryClient } from './QueryProvider';
import { emitLeagueDataUpdated, type LeagueDataUpdatedDetail } from '../utils/leagueDataEvents';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface AuthContextType {
  user: Profile | null;
  team: TeamWithDetails | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // v6.11.4 — true cuando se está viendo una edición finalizada (modo solo-lectura)
  readOnly: boolean;
  login: (dni: number, targetTeamId?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateTeam: () => Promise<void>;
  isAdminLoggedIn: boolean;
  adminRole: 'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN' | null;
  adminVenueId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

// ============================================================================
// Detección de cambios estructurales del equipo (v6.11.43)
// ============================================================================
// Campos cuyo cambio invalida lo que el participante está viendo: la zona
// (define rivales, tabla y grupo de WhatsApp), la categoría, la edición y el
// estado del equipo. Un cambio en cualquiera de estos no puede quedar esperando
// a que el usuario recargue a mano.
interface TeamIdentity {
  id: string | null;
  zone: string;
  category: string;
  league: string;
  status: string;
}

const teamIdentity = (team: TeamWithDetails | null | undefined): TeamIdentity => ({
  id: team?.id ?? null,
  zone: team?.zone_group ?? '',
  category: team?.category_id ?? '',
  league: team?.league_id ?? '',
  status: (team?.status as string) ?? '',
});

/**
 * Qué cambió entre dos fotos del equipo, en un mensaje listo para mostrar.
 * `null` = nada relevante cambió (o es la primera foto: no hay con qué comparar).
 */
const describeTeamChange = (
  prev: TeamIdentity | null,
  next: TeamIdentity
): LeagueDataUpdatedDetail | null => {
  // Sin foto previa, o sin equipo en alguno de los dos lados: no hay nada que
  // anunciar (arranque de sesión, logout, jugador todavía sin equipo).
  if (!prev || !prev.id || !next.id) return null;

  // Cambió el equipo entero (cambio de edición vía hub, alta en un equipo nuevo):
  // el resto de las diferencias son consecuencia de eso, no vale desglosarlas.
  if (prev.id !== next.id) {
    return { message: 'Cambiaste de equipo. Ya cargamos sus datos.', reason: 'team' };
  }

  if (prev.zone !== next.zone && next.zone) {
    return {
      message: prev.zone
        ? `Tu equipo pasó a la ${next.zone}.`
        : `¡Ya tenés zona! Tu equipo juega en la ${next.zone}.`,
      reason: 'zone',
    };
  }

  if (prev.category !== next.category) {
    return { message: 'Cambió la categoría de tu equipo.', reason: 'category' };
  }

  if (prev.league !== next.league) {
    return { message: 'Cambió la edición de la liga que estás viendo.', reason: 'league' };
  }

  if (prev.status !== next.status) {
    return { message: 'Cambió el estado de tu equipo.', reason: 'team' };
  }

  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Profile | null>(null);
  const [team, setTeam] = useState<TeamWithDetails | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminRole, setAdminRole] = useState<'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN' | null>(null);
  const [adminVenueId, setAdminVenueId] = useState<string | null>(null);

  // Última foto conocida del equipo, para detectar cambios estructurales entre
  // refrescos. `null` hasta la primera sesión aplicada (no se anuncia el arranque).
  const teamIdentityRef = useRef<TeamIdentity | null>(null);

  /**
   * Punto ÚNICO por el que entra una sesión al estado de React (arranque, login,
   * refresco manual, realtime, refresco al volver del segundo plano).
   *
   * Con `announce` compara contra la foto anterior y, si cambió algo estructural
   * (zona, categoría, edición, equipo, estado), invalida las queries cacheadas y
   * emite el evento que levanta el cartel "se actualizó tu info". Sin `announce`
   * sólo actualiza la foto — es el caso del arranque, donde no hay nada que
   * avisar porque el usuario recién abre la app.
   */
  const applySession = useCallback((session: UserSession | null, announce = false) => {
    if (!session) return;

    setUser(session.profile);
    setTeam(session.team || null);
    setReadOnly(session.readOnly ?? false);

    const prev = teamIdentityRef.current;
    const next = teamIdentity(session.team);
    teamIdentityRef.current = next;

    if (!announce) return;

    const change = describeTeamChange(prev, next);
    if (!change) return;

    // Los datos derivados (partidos, tabla, rivales pendientes, standings) se
    // calculan contra la zona/categoría vieja: hay que tirarlos todos.
    queryClient.invalidateQueries();
    emitLeagueDataUpdated(change);
  }, []);

  // Cargar sesión al iniciar
  useEffect(() => {
    loadSession();
    // Sólo al montar: `loadSession` se re-crea en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================================
  // Refresco al volver del segundo plano (v6.11.43)
  // ==========================================================================
  // El listener de Realtime de abajo cubre los cambios que ocurren con la app
  // VIVA. No cubre los que ocurren mientras está dormida: la PWA en segundo plano
  // pierde el websocket y Realtime no reenvía lo perdido al reconectar. Era el
  // agujero por el que se colaba el caso reportado — arranca la temporada, se
  // asignan las zonas, y la app que quedó abierta sigue mostrando el estado
  // previo hasta que el usuario recarga a mano.
  //
  // Se refresca al recuperar visibilidad/foco/conexión (throttle 60s para no
  // pegarle a cada `focus` del escritorio) y, además, cada 5 minutos con la app
  // en primer plano, que cubre al que la deja abierta mirando la pantalla.
  useAppResume(
    () => {
      refreshSession(false)
        .then((fresh) => applySession(fresh, true))
        .catch((e) => console.error('Error refreshing session on resume:', e));
    },
    { enabled: !!user, throttleMs: 60_000, intervalMs: 5 * 60_000 }
  );

  // Validar sesión de admin al cargar (SOLO si estamos en ruta admin)
  useEffect(() => {
    const validateAdmin = async () => {
      const path = window.location.pathname;
      const isAdminRoute = path.startsWith('/admin-liga') || path.startsWith('/admin-predios');

      // Solo validar si estamos en ruta admin
      if (!isAdminRoute) {
        return;
      }

      const storedToken = localStorage.getItem('admin_session_token');
      if (storedToken) {
        try {
          const sessionInfo = await validateAdminSession(storedToken);
          if (sessionInfo) {
            setIsAdminLoggedIn(true);
            setAdminRole(sessionInfo.role);
            setAdminVenueId(sessionInfo.venueId);
          } else {
            localStorage.removeItem('admin_session_token');
            setIsAdminLoggedIn(false);
            setAdminRole(null);
            setAdminVenueId(null);
          }
        } catch (error) {
          console.error('Error validating admin session in AuthProvider:', error);
          localStorage.removeItem('admin_session_token');
          setIsAdminLoggedIn(false);
          setAdminRole(null);
          setAdminVenueId(null);
        }
      }
    };
    validateAdmin();
  }, []);

  // Supabase Realtime - Escuchar cambios en perfil y equipo
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔄 Setting up Realtime listeners for user:', user.id);

    // Listener para cambios en perfil del usuario
    const profileChannel = supabase
      .channel(`profile:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('📥 Profile updated via Realtime:', payload.new);
          // v6.11.213: se FUSIONA sobre el perfil que ya teníamos. Cuando el acceso
          // público deja de ver dni/phone/birth_date, Realtime manda la fila sin
          // esas columnas; reemplazar el perfil entero borraba el DNI de la sesión
          // (y con él el re-ingreso de refreshSession).
          const changes = payload.new as Partial<Profile>;
          setUser((prev) => (prev ? { ...prev, ...changes } : prev));

          // Actualizar sesión en localStorage
          const session = getCurrentSession();
          if (session) {
            saveSession({ ...session, profile: { ...session.profile, ...changes } });
          }
        }
      )
      .subscribe((status) => {
        console.log('Profile channel status:', status);
      });

    // Listener para cambios en equipo
    let teamChannel: RealtimeChannel | null = null;
    if (user.current_team_id) {
      teamChannel = supabase
        .channel(`team:${user.current_team_id}`)
        .on(
          'postgres_changes',
          {
            event: '*',  // INSERT, UPDATE, DELETE
            schema: 'public',
            table: 'teams',
            filter: `id=eq.${user.current_team_id}`
          },
          async (payload) => {
            console.log('📥 Team updated via Realtime:', payload);
            // Recargar datos completos del equipo
            await updateTeam();
          }
        )
        .subscribe((status) => {
          console.log('Team channel status:', status);
        });
    }

    return () => {
      console.log('🔌 Unsubscribing from Realtime channels');
      profileChannel.unsubscribe();
      teamChannel?.unsubscribe();
    };
    // `updateTeam` fuera de deps a propósito: se re-crea en cada render y
    // re-suscribiría los canales de Realtime sin parar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.current_team_id]);

  const loadSession = async () => {
    try {
      const session = getCurrentSession();
      if (session) {
        setUser(session.profile);
        setReadOnly(session.readOnly ?? false);

        // Cargar equipo si existe
        let initialTeam: TeamWithDetails | null = session.team ?? null;
        if (session.team) {
          setTeam(session.team);
        } else if (session.profile.current_team_id) {
          // Timeout: este await está DENTRO del try/finally que resetea `isLoading`.
          // Una promesa que nunca settlea (fetch congelado al reanudar la PWA, red
          // que se durmió) no llega nunca al `finally` → `isLoading` se queda en true
          // y el splash de arranque gira para siempre. Es el único await del boot de
          // auth que había quedado sin acotar. Fallar acá es inocuo: el refresco en
          // segundo plano de abajo vuelve a resolver el equipo.
          const teamData = await withTimeout(
            getTeamById(session.profile.current_team_id),
            15000,
            'loadSession/getTeamById',
          ).catch((e) => {
            console.error('Error loading team on session load:', e);
            return null;
          });
          if (teamData) {
            setTeam(teamData);
            initialTeam = teamData;
          }
        }

        // Foto inicial del equipo: es la base contra la que los refrescos
        // posteriores detectan un cambio. Sin esto, el primer refresco compararía
        // contra `null` y no anunciaría nada.
        teamIdentityRef.current = teamIdentity(initialTeam);

        // v6.11.12 — refresco en segundo plano de los flags de sesión
        // (editionFinished/readOnly) contra la DB. Sin esto, una sesión persistida
        // vieja podía seguir tratando una edición YA archivada como activa (o al
        // revés), impidiendo ver la nueva edición / inscribirse. No bloquea el
        // pintado inicial y NO desloguea ante error de red (logoutOnError=false).
        refreshSession(false)
          .then((fresh) => applySession(fresh, false))
          .catch((e) => console.error('Error refreshing session on load:', e));

        // Actualizar last_access del usuario (sesión existente).
        // Fire-and-forget A PROPÓSITO: es una métrica secundaria y NO debe bloquear
        // el arranque. Si se `await`-ea y la RPC se cuelga (red/DB lenta), el
        // `finally` que resetea `isLoading` nunca corre → splash de arranque infinito
        // (el usuario "no ve ni para poner el DNI"). Por eso no la esperamos.
        void (supabase.rpc as any)('update_user_last_access', { p_user_id: session.profile.id })
          .then(() => console.log("✅ Last access updated on session load"))
          .catch((e: any) => console.error('Error updating last access:', e));
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setUser(null);
      setTeam(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (dni: number, targetTeamId?: string) => {
    setIsLoading(true);
    try {
      // Timeout: sin esto, un login RPC colgado deja `isLoading` en true y el
      // spinner del "CONTINUAR" / ingreso por DNI gira para siempre.
      const session = await withTimeout(loginWithDNI(dni, targetTeamId), 20000, 'loginWithDNI');
      // Sin `announce`: el usuario acaba de entrar, no hay "cambio" que avisarle.
      applySession(session, false);

      // Actualizar last_access del usuario — fire-and-forget (métrica secundaria).
      // Si se esperara y la RPC se colgara, `isLoading` quedaría en true y el
      // ingreso por DNI se trabaría en el spinner.
      void (supabase.rpc as any)('update_user_last_access', { p_user_id: session.profile.id })
        .then(() => console.log("✅ Last access updated"))
        .catch((e: any) => console.error('Error updating last access:', e));

      console.log("✅ Auth state updated successfully");
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      logoutService();
      setUser(null);
      setTeam(null);
      setReadOnly(false);
      teamIdentityRef.current = null;
      setIsAdminLoggedIn(false);
      setAdminRole(null);
      setAdminVenueId(null);
      localStorage.removeItem('admin_session_token'); // Asegurarse de limpiar el token de admin
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTeam = async () => {
    console.log("🔄 updateTeam called - refreshing session...");
    try {
      const session = await refreshSession();
      console.log("📦 Session refreshed:", {hasUser: !!session?.profile, hasTeam: !!session?.team, teamId: session?.team?.id, teamName: session?.team?.name});
      if (session) {
        // `announce`: updateTeam es también el handler del Realtime de `teams`, o
        // sea el camino por el que llega la asignación de zona con la app viva.
        applySession(session, true);
        console.log("✅ Auth state updated successfully");
      }
    } catch (error) {
      console.error('Error updating team:', error);
    }
  };

    const value: AuthContextType = {
      user,
      team,
      isLoading,
      isAuthenticated: !!user,
      readOnly,
      login,
      logout,
      updateTeam,
      isAdminLoggedIn,
      adminRole,
      adminVenueId,
    };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
